// Dependency-free A4 paper edit PDF. Uses PDF's built-in WinAnsi font.
const W=595.28,H=841.89,M=46;
const cp={'€':128,'‚':130,'ƒ':131,'„':132,'…':133,'†':134,'‡':135,'ˆ':136,'‰':137,'Š':138,'‹':139,'Œ':140,'Ž':142,'‘':145,'’':146,'“':147,'”':148,'•':149,'–':150,'—':151,'˜':152,'™':153,'š':154,'›':155,'œ':156,'ž':158,'Ÿ':159};
const toAnsi=text=>[...String(text??'')].map(c=>{
  if(Object.hasOwn(cp,c))return String.fromCharCode(cp[c]);
  const n=c.charCodeAt(0);
  return n>=32&&n<=255?c:'?';
}).join('');
const pdfEscape=text=>toAnsi(text).replace(/\\/g,'\\\\').replace(/\(/g,'\\(').replace(/\)/g,'\\)');
const approximate=(text,size)=>[...toAnsi(text)].reduce((n,c)=>n+(c===' '?0.27:/[ijlI1.,:;!]/.test(c)?0.25:/[mMwW@]/.test(c)?0.83:/[rft]/.test(c)?0.35:/[A-Z0-9]/.test(c)?0.62:0.50),0)*size;
const timecode=n=>{const v=Math.floor(Math.max(0,Number(n)||0));return [Math.floor(v/3600),Math.floor(v/60)%60,v%60].map(x=>String(x).padStart(2,'0')).join(':');};
export const paperPdfFilename=title=>(String(title||'story').replace(/[^a-z0-9_-]+/gi,'-').replace(/^-+|-+$/g,'').slice(0,65)||'story')+'-paper-edit.pdf';
export function buildPaperPdf(paper,title='Takefold Paper Edit'){
  if(!paper?.blocks?.length)throw new Error('Add a Story passage before exporting a PDF.');
  const pages=[];let ops=[],y=H-M;
  const at=(text,x,size,bold=false,col='0.16 0.19 0.17')=>{
    ops.push(col+' rg BT /'+(bold?'F2':'F1')+' '+size+' Tf 1 0 0 1 '+x.toFixed(2)+' '+y.toFixed(2)+' Tm ('+pdfEscape(text)+') Tj ET');
  };
  const next=()=>{pages.push(ops.join('\n')+'\n');ops=[];y=H-M;at('TAKEFOLD  /  PAPER EDIT',M,9,true,'0.08 0.50 0.25');y-=28;};
  const enough=n=>{if(y-n<M+18)next();};
  const rule=()=>{ops.push('0.78 0.84 0.79 RG 0.6 w '+M+' '+y.toFixed(2)+' m '+(W-M)+' '+y.toFixed(2)+' l S');y-=17;};
  const write=(txt,size=10,leading=15,bold=false,color='0.16 0.19 0.17',after=0)=>{
    const maxWidth=W-2*M;let line='';
    const flush=()=>{enough(leading+4);at(line,M,size,bold,color);y-=leading;line='';};
    for(const part of String(txt||'').split(/\s+/).filter(Boolean)){
      const joined=line?line+' '+part:part;
      if(approximate(joined,size)<=maxWidth){line=joined;continue;}
      if(line)flush();
      if(approximate(part,size)<=maxWidth){line=part;continue;}
      for(const c of [...part]){if(line&&approximate(line+c,size)>maxWidth)flush();line+=c;}
    }
    if(line)flush();y-=after;
  };
  at('TAKEFOLD  /  EDITORIAL PAPER EDIT',M,9,true,'0.08 0.50 0.25');y-=33;
  write(title,19,23,true,undefined,8);
  const total=paper.blocks.reduce((n,b)=>n+Math.max(0,b.end-b.start),0);
  write(paper.blocks.length+' passages  |  '+timecode(total)+' selected footage  |  '+new Set(paper.blocks.map(b=>b.sourceId)).size+' interviews',9,13,false,'0.40 0.44 0.42',12);
  rule();
  const headings=paper.sectionHeadings||[];
  const section=index=>{
    for(const h of headings.filter(x=>x.beforeIndex===index&&x.title?.trim())){
      enough(55);y-=7;write(h.title.trim().toUpperCase(),12,17,true,'0.08 0.50 0.25',5);rule();
    }
  };
  const labels={must:'MUST USE',maybe:'MAYBE',avoid:'AVOID'};
  paper.blocks.forEach((b,i)=>{
    section(i);
    const source=paper.sources?.[b.sourceId];
    if(!source)throw new Error('A Story passage has a missing source interview.');
    if(!Number.isFinite(b.start)||!Number.isFinite(b.end)||b.end<=b.start)
      throw new Error('A Story passage has invalid source timecodes.');
    const quote=(b.text||source.words?.filter(w=>w.start<b.end&&w.end>b.start).map(w=>w.text).join(' ')||'').trim();
    if(!quote)throw new Error('A Story passage is missing transcript words.');
    enough(42);
    write(String(i+1).padStart(2,'0')+'  '+(source.title||source.filename||'Interview')+'  |  IN '+timecode(b.start)+'  OUT '+timecode(b.end)+(labels[b.priority]?'  |  '+labels[b.priority]:''),8.6,13,true,'0.40 0.44 0.42',6);
    write(quote,10.2,15.4,false,undefined,6);
    if(b.editorialReason)write('EDITORIAL NOTE: '+b.editorialReason,8,11.8,false,'0.40 0.44 0.42',3);
    y-=11;
  });
  section(paper.blocks.length);enough(32);rule();
  write('Editor copy - source wording and timecodes should be checked before final delivery.',8,12,false,'0.40 0.44 0.42');
  pages.push(ops.join('\n')+'\n');
  const objects=[];const add=s=>(objects.push(s),objects.length);
  const catalog=add(''),pagesId=add('');
  const regular=add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>');
  const bold=add('<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>');
  const children=[];
  for(const stream of pages){
    const content=add('<< /Length '+stream.length+' >>\nstream\n'+stream+'endstream');
    children.push(add('<< /Type /Page /Parent '+pagesId+' 0 R /MediaBox [0 0 '+W+' '+H+'] /Resources << /Font << /F1 '+regular+' 0 R /F2 '+bold+' 0 R >> >> /Contents '+content+' 0 R >>'));
  }
  objects[catalog-1]='<< /Type /Catalog /Pages '+pagesId+' 0 R >>';
  objects[pagesId-1]='<< /Type /Pages /Count '+children.length+' /Kids ['+children.map(n=>n+' 0 R').join(' ')+'] >>';
  let pdf='%PDF-1.4\n%\xE2\xE3\xCF\xD3\n';const offsets=[];
  for(let i=0;i<objects.length;i++){offsets.push(pdf.length);pdf+=(i+1)+' 0 obj\n'+objects[i]+'\nendobj\n';}
  const start=pdf.length;
  pdf+='xref\n0 '+(objects.length+1)+'\n0000000000 65535 f \n';
  for(const offset of offsets)pdf+=String(offset).padStart(10,'0')+' 00000 n \n';
  pdf+='trailer\n<< /Size '+(objects.length+1)+' /Root '+catalog+' 0 R >>\nstartxref\n'+start+'\n%%EOF\n';
  return Uint8Array.from(pdf,c=>c.charCodeAt(0));
}
